import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';
import createAsyncGetters from '../../config/asyncGetters/createAsyncGetters';
import {
  Match2ModelTournamentPlayer,
  Match2ModelTournamentData,
} from './match2-tournament.actions';
import * as getters from './match2-tournament.getters';
import {
  TournamentType,
  match2TournamentRuleset,
  match2TournamentRuleset as ruleset,
} from './match2-tournament.ruleset';
import { NumberFilters } from '@play-co/replicant';
import { AvatarFrameId } from 'src/replicant/profile/frames';

type OpponnentScores = {
  [key: string]: { score: number; scoreUpdatedAt: number };
};

export const match2TournamentsAsyncGetters = createAsyncGetters({
  /**
   * Search players and fetch updated player scores for a tournament event
   */
  getMatch2TournamentOpponentScores: async (opts: { eventId: string }, api) => {
    const { eventId } = opts;
    const state = await api.getOwnState();

    // Find out the tournament event by id
    const eventState = state.match2?.tournament?.events[eventId];

    // Return null if there is no event with given id
    if (!eventState) {
      return null;
    }

    // List of players ids in the tournament event
    const opponentList = eventState.opponentList as string[];
    const opponnentScores: OpponnentScores = {};

    // Fetch the opponents scores
    const opponents = await api.searchPlayers({
      where: {
        id: {
          isOneOf: opponentList,
        },
      },
      limit: opponentList.length,
    });

    // Map the opponents to the opponnentScores object
    return opponents.results.reduce((acc, opponent) => {
      // extract the score/scoreUpdatedAt from the tournament event (computed property)
      const tournament = opponent.match2Tournaments.find(
        (event) => eventId === event.eventId,
      );
      acc[opponent.id] = {
        score: tournament?.score ?? 0,
        scoreUpdatedAt: tournament?.scoreUpdatedAt ?? 0,
      };
      return acc;
    }, opponnentScores);
  },

  /**
   * Fetch the current tournament event.
   */
  getMatch2TournamentEvent: async ({ eventId }: { eventId: string }, api) => {
    const state = await api.getOwnState();

    const period = getters.getTournamentPeriodById(state, eventId);

    if (!period) {
      throw new Error(`[getMatch2TournamentEvent]: Tournament is not defined`);
    }

    const eventState = state.match2.tournament.events[period.eventId];
    let matchMakingCriteria = eventState?.matchMakingCriteria ?? 'base';

    const now = api.date.now();
    const flyingHighTournament = getters.isFlyingHighTournamentEnabled(
      state,
      now,
    );
    const whereClauses: {
      match2Level?: NumberFilters;
    } = {};

    const periodV2 = getters.getTournamentPeriodForSchedule(
      match2TournamentRuleset.weeklyScheduleV2,
      new Date(eventId).getTime(),
    );

    if (periodV2 && flyingHighTournament) {
      whereClauses.match2Level = {
        greaterThanOrEqual: ruleset.flyingHighUnlockLevel,
      };
    } else {
      whereClauses.match2Level = {
        lessThan: ruleset.flyingHighUnlockLevel,
      };
    }

    if (!eventState) {
      api.reportError(new Error('No tournament event state'));

      return null;
    }

    const opponnentsLookup = (
      await api.searchPlayers({
        where: {
          id: {
            isOneOf: [
              ...state.match2.tournament.events[period.eventId].opponentList,
            ],
          },
        },
        limit:
          state.match2.tournament.events[period.eventId].opponentList.length,
      })
    ).results;

    // -- Generate the leaderboard (unsorted)
    const leaderboard: Match2ModelTournamentPlayer[] = opponnentsLookup.map(
      (opponent) => {
        const tournament = opponent.match2Tournaments?.find(
          (event) => event.eventId === period.eventId,
        );
        const player = {
          id: opponent.id,
          score: tournament?.score || 0,
          scoreUpdatedAt: tournament?.scoreUpdatedAt || 0,
          name: opponent.publicProfileName,
          avatar: opponent.publicProfilePhoto,
          frame: opponent.profileAvatarFrame as AvatarFrameId,
          startedAt: tournament
            ? opponent.match2TournamentLastStartedAt
            : api.date.now(), // default to now if no tournament
        };
        return player;
      },
    );

    // generate the player object
    const player = {
      id: state.id,
      score: eventState?.player.score ?? 0,
      scoreUpdatedAt: eventState?.player.scoreUpdatedAt ?? 0,
      avatar: state.publicProfile?.photo || state.profile?.photo || '',
      frame: getPlayerProfileAvatarFrame(state),
      name: state.publicProfile?.name || state.profile?.name || '',
      startedAt: state.match2.tournament.lastStartedAt ?? 0,
    };
    leaderboard.push(player);

    leaderboard.sort((a, b) => a.scoreUpdatedAt - b.scoreUpdatedAt);
    leaderboard.sort((a, b) => b.score - a.score);

    const currentRank =
      leaderboard.findIndex((player) => player.id === state.id) + 1;

    // Check if tournament has ended
    const ended = period.end < api.date.now();

    // If ended, set the finalRank
    const finalRank = ended ? currentRank : undefined;

    const data: Match2ModelTournamentData = {
      endDate: period.end,
      claimed: !!eventState?.claimed,
      currentRank,
      finalRank: eventState?.finalRank ?? finalRank,
      player,
      leaderboard,
      matchMakingCriteria,
      type: (eventState?.type as TournamentType) ?? 'star', // Fallback to star tournament if type is not defined - ie. on older tournament schema
    };

    return data;
  },
});
